import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth, useClerk } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "../contexts/UserContext.types";
import {
  applicationKey, bindSessionOwner, clearBackendSession, healthcareRequest,
  HealthcareApiError, isRecord, rememberBackendSession,
} from "./healthcare-api";

interface Account { owner: string; user: User | null; token: string | null; }
export function useHealthcareAccount() {
  const auth = useAuth();
  const clerk = useClerk();
  const queryClient = useQueryClient();
  const clerkRef = useRef(clerk);
  clerkRef.current = clerk;
  const getOwner = useCallback(() => clerkRef.current.session?.id ?? null, []);
  const controller = useRef<AbortController | null>(null);
  const revision = useRef(0);
  const [account, setAccount] = useState<Account | null>(null);
  const [failure, setFailure] = useState<{owner: string; message: string} | null>(null);
  const [working, setWorking] = useState(false);

  const reconnect = useCallback(async () => {
    const session = clerkRef.current.session;
    if (!session) {
      setFailure({owner: auth.sessionId ?? "", message: "Your sign-in session is still loading. Please try again."});
      throw new Error("Please sign in before continuing.");
    }
    const owner = session.id;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const attempt = ++revision.current;
    const current = () => !abort.signal.aborted && attempt === revision.current && getOwner() === owner;
    clearBackendSession();
    setAccount(null);
    setFailure(null);
    setWorking(true);
    try {
      const getToken = async () => {
        if (!current()) throw new Error("Your account changed. Please try again.");
        const token = await session.getToken();
        if (!token) throw new Error("Your sign-in session expired. Please sign in again.");
        return token;
      };
      const sync = await healthcareRequest("/api/v2/auth/sync", {
        token: await getToken(), method: "POST", body: {}, signal: abort.signal,
      });
      if (!isRecord(sync) || sync.success !== true || !isRecord(sync.user)
        || typeof sync.user.id !== "string" || !sync.user.id.trim()) {
        throw new Error("Your account identity could not be confirmed.");
      }
      let result: unknown;
      try {
        result = await healthcareRequest("/api/v2/auth/session", {
          token: await getToken(), method: "POST", body: {applicationKey: applicationKey()}, signal: abort.signal,
        });
      } catch (error) {
        if (error instanceof HealthcareApiError && error.status === 403 && error.code === "APPLICATION_ACCESS_REQUIRED") {
          if (current()) setAccount({owner, user: null, token: null});
          return;
        }
        throw error;
      }
      const data = isRecord(result) ? result.data : null;
      if (!isRecord(result) || result.success !== true || !isRecord(data)
        || typeof data.accessToken !== "string" || !data.accessToken.trim()
        || typeof data.sessionId !== "string" || !data.sessionId.trim()
        || !isRecord(data.user) || data.user.id !== sync.user.id) {
        throw new Error("Your application session could not be confirmed.");
      }
      // A successful scoped backend session, never Clerk metadata, authorises this portal.
      const key = applicationKey();
      const identity = data.user;
      const realUser = clerkRef.current.user;
      const name = typeof identity.name === "string" ? identity.name
        : typeof identity.fullName === "string" ? identity.fullName : realUser?.fullName ?? "";
      const user: User = {
        id: sync.user.id,
        name,
        email: typeof identity.email === "string" ? identity.email : realUser?.primaryEmailAddress?.emailAddress ?? "",
        avatar: realUser?.imageUrl,
        role: key === "hospital" ? (["doctor", "healthcare_professional"].includes(String(identity.role)) ? "doctor" : "partner") : key === "lab" ? "laboratory" : "insurance",
        hasCompletedOnboarding: true,
      };
      if (!current()) return;
      rememberBackendSession(owner, data.accessToken);
      setAccount({owner, user, token: data.accessToken});
    } catch (error) {
      if (current()) {
        clearBackendSession();
        setFailure({owner, message: error instanceof Error ? error.message : "Unable to connect your account."});
      }
      throw error;
    } finally {
      if (current()) setWorking(false);
    }
  }, [getOwner, auth.sessionId]);

  useEffect(() => {
    bindSessionOwner(getOwner);
    const expire = () => {
      const owner = getOwner();
      if (!owner) return;
      ++revision.current;
      controller.current?.abort();
      setAccount(null);
      setWorking(false);
      setFailure({owner, message: "Your application session expired. Please reconnect your account."});
    };
    window.addEventListener("gluviacare:session-expired", expire);
    return () => {
      window.removeEventListener("gluviacare:session-expired", expire);
      ++revision.current;
      controller.current?.abort();
      clearBackendSession();
    };
  }, [getOwner]);
  useEffect(() => {
    ++revision.current;
    controller.current?.abort();
    clearBackendSession();
    queryClient.clear();
    setAccount(null);
    setFailure(null);
    setWorking(false);
    if (auth.isLoaded && auth.isSignedIn) void reconnect().catch(() => {});
  }, [auth.isLoaded, auth.isSignedIn, auth.sessionId, reconnect, queryClient]);

  const logout = useCallback(async () => {
    ++revision.current;
    controller.current?.abort();
    clearBackendSession();
    queryClient.clear();
    setAccount(null);
    await clerkRef.current.signOut({redirectUrl: "/login"});
  }, [queryClient]);
  const owner = auth.sessionId;
  const valid = auth.isSignedIn && account?.owner === owner ? account : null;
  const error = auth.isSignedIn && failure && failure.owner === owner ? failure.message : "";
  return {
    user: valid?.user ?? null,
    token: valid?.token ?? null,
    loading: !auth.isLoaded || Boolean(auth.isSignedIn && (working || (!valid && !error))),
    error, reconnect, logout,
  };
}
